import { ApolloClient } from 'apollo-client';
import { setContext } from 'apollo-link-context';
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { createLink } from 'apollo-absinthe-upload-link';
import { ApolloLink } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { get } from 'lodash-es';
import fetch from 'node-fetch';

/**
 * Generic Function for generating Apollo client used in Kargo ecosystem.
 * @param {string} uri URL for API to call
 * @param {boolean} skipAuth Skip authentication
 * @param {function} bearerTokenSetter 1-arity Function to set bearer token data for authorization
 *  and header data in general, first argument is original headers object data,
 *  return Object { headers: modifiedHeadersObjectData }
 * @param {function} appRoleSetter 0-arity Function to set app role as header,
 *  used for Logic differentiation in Load, Profile service, return appRole String
 * @param {function} companyKsuidSetter 0-arity Function to set company ksuid as header,
 *  used for Authentication purposes, return companyKsuid String
 * @param {function} applicationIdSetter 0-arity Function to set applicationId data as header,
 *  used for differenting products on shared backend metric, return applicationId String
 * @param {function} onBackendError 2-arity Function to handle backend error,
 *  first argument is error object, second is operation
 * @param {function} onNetworkError 1-arity Function to handle network error, first argument is error object
 * @param {function} onUnexpectedError 0-arity Function to handle unexpected error
 * @returns {ApolloClient} Apollo client
 */
export const generateClient = ({
  uri = '',
  skipAuth = false,
  bearerTokenSetter = e => e,
  appRoleSetter = e => e,
  companyKsuidSetter = e => e,
  applicationIdSetter = e => e,
  onBackendError = e => e,
  onNetworkError = e => e,
  onUnexpectedError = e => e,
} = {}) => {
  const authLink = setContext(async (_, { headers }) => bearerTokenSetter(headers));
  const appRoleLink = setContext(
    (
      _,
      {
        headers,
        appRole = appRoleSetter(),
        companyKsuid = companyKsuidSetter(),
        applicationId = applicationIdSetter(),
      }
    ) => ({
      headers: {
        ...headers,
        ...(appRole
          ? {
            'x-kargo-role': appRole,
          }
          : {}),
        ...(companyKsuid
          ? {
            'x-kargo-company-ksuid': companyKsuid,
          }
          : {}),
        ...(applicationId
          ? {
            'x-kargo-app': applicationId,
          }
          : {}),
      },
    })
  );

  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('ERROR logged from @kargotech/tms-common/Services/apollo.js:\n', {
        graphQLErrors,
        networkError,
        operation
      });
    }

    // User access token has expired
    if (get(graphQLErrors, '[0].message') === 'unauthorized' && !skipAuth) {
      const oldHeaders = operation.getContext().headers;
      bearerTokenSetter(oldHeaders)
        .then(newContext => {
          operation.setContext(newContext);
          return forward(operation);
        });
    }

    // Error from backend
    if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
      graphQLErrors.forEach(error => onBackendError(error, operation));
      return operation.variables?.SKIP_BACKEND_ERROR_RETRY ? null : forward(operation);
    }

    if (networkError) {
      onNetworkError(networkError);
      return forward(operation);
    }

    // Unexpected error
    onUnexpectedError();
    return forward(operation);
  });

  const link = createLink({ uri, fetch });

  // Temporary solution, need to fetch possibleTypes from api
  const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData: {
      __schema: {
        types: []
      }
    }
  });

  return new ApolloClient({
    link: ApolloLink.from([
      errorLink,
      !skipAuth && authLink,
      appRoleSetter && appRoleLink,
      link
    ].filter(Boolean)),
    cache: new InMemoryCache({
      fragmentMatcher
    })
  });
};

export default {};
